// src/components/layout/Sidebar.jsx
import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Collapse,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Dashboard,
  Group,
  Settings,
  AccountCircle,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  BarChart,
  Person,
  Security,
  CreditCard,
  BusinessCenter,
  People,
  HelpOutline,
  Code,
  Assessment
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getVersionString } from '../../utils/buildInfo';
import versionService from '../../services/versionService';
import LPSLogo from '../LPSLogo';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [openSubMenus, setOpenSubMenus] = React.useState({});
  const [version, setVersion] = React.useState(getVersionString()); // Initialize with local version
  const [collapsed, setCollapsed] = React.useState(false);

  // Load collapsed state from localStorage
  React.useEffect(() => {
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState) {
      setCollapsed(JSON.parse(savedState));
    }
  }, []);

  // Fetch version from API
  React.useEffect(() => {
    const fetchVersion = async () => {
      try {
        const versionString = await versionService.getVersion();
        setVersion(versionString);
      } catch (error) {
        // Keep using the local version if API fails
        console.error('Failed to fetch version from API:', error);
      }
    };
    fetchVersion();
  }, []);

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
    // Close all sub-menus when collapsing
    if (newState) {
      setOpenSubMenus({});
    }
  };

  const toggleSubMenu = (menuId) => {
    if (collapsed) return; // Don't allow sub-menu opening when collapsed
    
    setOpenSubMenus(prev => {
      const isCurrentlyOpen = prev[menuId];
      
      // If clicking on an already open menu, just close it
      if (isCurrentlyOpen) {
        return {
          ...prev,
          [menuId]: false
        };
      }
      
      // Otherwise, close all menus and open only this one
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[menuId] = true;
      
      return newState;
    });
  };

  const handleNavigation = (path, hasSubItems = false, menuId = null) => {
    if (hasSubItems && !collapsed) {
      toggleSubMenu(menuId);
    } else {
      navigate(path);
    }
  };

  // Enhanced navigation to handle tab switching
  const navigateToTab = (basePath, tabParam) => {
    navigate(`${basePath}?tab=${tabParam}`);
  };

  // Persona-composed navigation (see docs/webapp-ia.md): the same platform
  // presents a different, smaller world per role. Vertical noun pack renames
  // Subjects -> Patients/Cases/... via /api/lps/org/vertical.
  const [nouns, setNouns] = React.useState({ subject: 'Subject', subjects: 'Subjects', session: 'Session', sessions: 'Sessions' });
  React.useEffect(() => {
    import('../../services/lpsService').then(({ default: lps }) =>
      lps.getVertical()
        .then((v) => import('../../utils/verticalNouns')
          .then(({ nounsFor }) => setNouns(nounsFor(v.vertical))))
        .catch(() => {}));
  }, []);

  const getMenuItems = () => {
    const role = user?.role;
    const items = [];
    const clinicianCore = [
      { id: 'home', text: 'My Day', icon: <Dashboard />, path: '/home', subItems: [] },
      { id: 'subjects', text: nouns.subjects, icon: <Group />, path: '/subjects', subItems: [] },
      { id: 'recordings', text: `All ${nouns.sessions}`, icon: <BarChart />, path: '/recordings', subItems: [] },
      { id: 'chat', text: 'Ask My Records', icon: <People />, path: '/chat', subItems: [] },
      { id: 'practice', text: 'My Practice', icon: <Assessment />, path: '/practice', subItems: [] },
    ];

    if (role === 'user' || role === 'manager') {
      items.push(...clinicianCore);
    }

    if (['super_admin', 'admin'].includes(role)) {
      items.push(
        { id: 'subjects', text: nouns.subjects, icon: <Group />, path: '/subjects', subItems: [] },
        { id: 'recordings', text: `All ${nouns.sessions}`, icon: <BarChart />, path: '/recordings', subItems: [] },
        { id: 'chat', text: 'Ask Records', icon: <People />, path: '/chat', subItems: [] },
        {
          id: 'security', text: 'Security & Audit', icon: <Security />, path: '/security',
          subItems: [
            { text: 'Audit Trail', param: 'audit' },
            { text: 'Auth Events', param: 'auth' }
          ]
        },
        {
          id: 'administration', text: 'Administration', icon: <BusinessCenter />, path: '/admin',
          subItems: [
            { text: 'User Management', param: 'users' },
            { text: 'Compliance', param: 'compliance' }
          ]
        },
        {
          id: 'org', text: 'Organization', icon: <Settings />, path: '/organization-lps',
          subItems: [
            { text: 'Industry Pack', param: 'vertical' },
            { text: 'Report Branding', param: 'branding' }
          ]
        }
      );
    }

    if (role === 'readonly') {
      items.push(
        {
          id: 'security', text: 'Audit', icon: <Security />, path: '/security',
          subItems: [
            { text: 'Audit Trail', param: 'audit' },
            { text: 'Auth Events', param: 'auth' }
          ]
        },
        { id: 'recordings', text: `All ${nouns.sessions}`, icon: <BarChart />, path: '/recordings', subItems: [] }
      );
    }

    items.push(
      { id: 'settings', text: 'Settings', icon: <Person />, path: '/settings', subItems: [] },
      { id: 'help', text: 'Help Center', icon: <HelpOutline />, path: '/help', subItems: [] }
    );

    return items;
  };

  // Check if current path matches menu item
  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Keep sub-menus closed by default, maintain state only when user explicitly opens them
  // No auto-expansion based on current path

  const currentWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: 'width 0.3s ease',
          overflowX: 'hidden'
        }
      }}
    >
      {/* Header with Logo and Collapse Button */}
      <Box sx={{ 
        p: collapsed ? 1 : 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: collapsed ? 'center' : 'space-between',
        height: 64, // Fixed height to match TopBar exactly
        position: 'relative',
        zIndex: 1300 // Ensure header is above toolbar
      }}>
        {collapsed ? (
          // Show just the logo icon when collapsed
          <Tooltip title="Click to expand sidebar" placement="right">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
            onClick={toggleCollapsed}>
              <LPSLogo compact height={32} color="white" />
            </Box>
          </Tooltip>
        ) : (
          <>
            <LPSLogo width={120} height={32} color="white" />
            <Tooltip title="Collapse sidebar" placement="bottom">
              <IconButton 
                onClick={toggleCollapsed}
                size="small"
                sx={{ 
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  width: 32,
                  height: 32,
                  '&:hover': { 
                    bgcolor: 'action.selected',
                    color: 'text.primary'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
      
      <Divider />
      
      <List sx={{ pt: 1 }}>
        {getMenuItems().map((item) => (
          (
            <React.Fragment key={item.id}>
              {/* Main Menu Item */}
              <Tooltip 
                title={collapsed ? item.text : ''} 
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItem
                  button
                  onClick={() => handleNavigation(item.path, !!item.subItems, item.id)}
                  selected={isPathActive(item.path)}
                  sx={{
                    py: 1.5,
                    px: collapsed ? 1.5 : 3,
                    mx: collapsed ? 1 : 0,
                    borderRadius: collapsed ? 2 : 0,
                    minHeight: 48,
                    '&.Mui-selected': {
                      bgcolor: 'primary.lighter',
                      '&:hover': {
                        bgcolor: 'primary.lighter'
                      }
                    },
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: collapsed ? 'auto' : 40,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  
                  {!collapsed && (
                    <>
                      <ListItemText 
                        primary={item.text}
                        sx={{ flex: 1 }}
                      />
                      
                      
                      {/* Expand/Collapse Icon */}
                      {item.subItems && (
                        openSubMenus[item.id] ? <ExpandLess /> : <ExpandMore />
                      )}
                    </>
                  )}
                </ListItem>
              </Tooltip>

              {/* Sub Menu Items */}
              {item.subItems && !collapsed && (
                <Collapse in={openSubMenus[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        button
                        key={subItem.param}
                        onClick={() => navigateToTab(item.path, subItem.param)}
                        sx={{
                          py: 1,
                          pl: 6,
                          pr: 3,
                          minHeight: 40,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <ListItemText 
                          primary={subItem.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              color: subItem.highlight ? 'primary.main' : 'text.secondary'
                            }
                          }}
                        />
                        
                        {/* Sub-item badges */}
                        {subItem.badge === 'new' && (
                          <Chip 
                            label="New" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          )
        ))}
      </List>

      {/* Footer with Version Info */}
      {!collapsed && (
        <Box sx={{ 
          mt: 'auto', 
          p: 2, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" display="block">
              {user?.email}
            </Typography>
            <Typography 
              variant="caption" 
              color="textSecondary" 
              sx={{ 
                fontSize: '0.7rem',
                fontFamily: 'monospace',
                opacity: 0.7
              }}
            >
              {version}
            </Typography>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};